/**
 * Sprint 207S — "Preview as a teen" modal.
 *
 * Opens on-demand from the Maya Manager Scripts tab. Replays the
 * scripted-message sequence for any trigger exactly the way a teen
 * would experience it on /conversations — Maya portrait, typing
 * indicator, 900ms inter-message pause, and (opt-in) real OpenAI TTS
 * audio via /api/tts/preview. Read-only: no DB writes, no peer / chat
 * session created, no production users disturbed.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, Play, RotateCcw, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const TRIGGER_LABELS = {
  first_session: 'First session',
  second_session: 'Second session',
  lapse_day_2: 'Day-2 silent (lapse)',
  post_challenge_complete: 'After a challenge complete',
  on_demand: 'On demand (manual)',
};

// Same OpenAI base-voice resolver as TEEN_VOICES (avaLibrary/data.js).
const VOICE_BASE = {
  tf1: 'nova', tf2: 'shimmer', tf3: 'nova', tf4: 'shimmer', tf5: 'coral',
  tf6: 'nova', tf7: 'fable', tf8: 'shimmer', tf9: 'coral', tf10: 'alloy',
  tm1: 'echo', tm2: 'onyx', tm3: 'echo', tm4: 'ash', tm5: 'onyx',
  tm6: 'echo', tm7: 'sage', tm8: 'ash', tm9: 'fable', tm10: 'onyx',
};

const MayaPreviewModal = ({ open, onClose, source = 'maya' }) => {
  const [trigger, setTrigger] = useState('first_session');
  const [identity, setIdentity] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [withAudio, setWithAudio] = useState(false);
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const abortRef = useRef(false);
  const audioRef = useRef(null);

  // Load identity + scripts whenever modal opens or trigger changes
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const identityReq = apiClient.request('/api/maya-manager/identity');
        const scriptsReq = source === 'templates'
          ? apiClient.request('/api/ava-templates/templates')
          : apiClient.request('/api/maya-manager/scripts');
        const [idResp, scResp] = await Promise.all([identityReq, scriptsReq]);
        if (cancelled) return;
        setIdentity(idResp);
        const raw = scResp.rows || [];
        const filtered = source === 'templates'
          ? raw
              .filter(t => t.trigger === trigger && t.is_active)
              .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
              .map(t => ({
                script_id: t.template_id,
                message: (t.message_template || '')
                  .replace(/\{teen_name\}/g, 'Sam')
                  .replace(/\{ava_name\}/g, idResp?.first_name || 'AvA'),
              }))
          : raw
              .filter(s => s.trigger_event === trigger && s.active)
              .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
        setScripts(filtered);
      } catch (e) {
        if (!cancelled) setScripts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, trigger, source]);

  // Clear playback state any time we close or change trigger.
  useEffect(() => {
    abortRef.current = true;
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; }
    setVisibleMessages([]);
    setTyping(false);
    setPlaying(false);
  }, [open, trigger]);

  const playOneAudio = async (text, voiceSlot) => {
    if (!withAudio) return;
    const baseVoice = VOICE_BASE[voiceSlot] || 'nova';
    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const resp = await fetch(`${apiUrl}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text, voice: baseVoice }),
      });
      const data = await resp.json();
      if (data?.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
        audioRef.current = audio;
        await new Promise((res) => {
          audio.onended = res;
          audio.onerror = res;
          audio.play().catch(res);
        });
      }
    } catch (_e) { /* fail-soft on audio */ }
  };

  const startPreview = async () => {
    abortRef.current = false;
    setVisibleMessages([]);
    setPlaying(true);
    // Start with the standard fallback greeting Maya would send before
    // her scripted messages fire (mirrors ConversationsChat.js flow).
    const firstName = (identity?.first_name) || 'Maya';
    const greeting = {
      id: 'preview_greeting',
      content: `Hi there — I'm ${firstName}. I'm here when you're ready.`,
    };
    setVisibleMessages([greeting]);
    await playOneAudio(greeting.content, identity?.voice_slot);

    for (const s of scripts) {
      if (abortRef.current) break;
      setTyping(true);
      await new Promise(r => setTimeout(r, 900));
      if (abortRef.current) break;
      setTyping(false);
      setVisibleMessages(prev => [...prev, { id: s.script_id, content: s.message }]);
      await playOneAudio(s.message, identity?.voice_slot);
    }
    setTyping(false);
    setPlaying(false);
  };

  const reset = () => {
    abortRef.current = true;
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; }
    setVisibleMessages([]);
    setTyping(false);
    setPlaying(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[80] p-4"
      data-testid="maya-preview-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {identity?.portrait_url && (
              <img src={identity.portrait_url} alt="Maya" className="w-9 h-9 rounded-full object-cover bg-gray-100" data-testid="maya-preview-portrait" />
            )}
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Preview as a teen</h3>
              <p className="text-xs text-gray-500">Watch your scripted Maya sequence the way a teen will</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500" data-testid="maya-preview-close">
            <X size={16} />
          </button>
        </div>

        {/* Controls */}
        <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center gap-3 bg-gray-50">
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-600">Trigger:</label>
            <select
              value={trigger}
              onChange={(e) => setTrigger(e.target.value)}
              disabled={playing}
              className="px-2 py-1 text-xs border border-gray-200 rounded-md bg-white disabled:opacity-50"
              data-testid="maya-preview-trigger-select"
            >
              {Object.entries(TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          <button
            onClick={() => setWithAudio(a => !a)}
            disabled={playing}
            className={`text-xs px-2.5 py-1 rounded-md inline-flex items-center gap-1 ${withAudio ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}
            data-testid="maya-preview-audio-toggle"
            title="Toggle real TTS audio playback"
          >
            {withAudio ? <Volume2 size={13} /> : <VolumeX size={13} />}
            {withAudio ? 'Audio ON' : 'Audio OFF'}
          </button>

          <div className="flex-1" />

          <button
            onClick={reset}
            disabled={playing}
            className="text-xs px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="maya-preview-reset"
          >
            <RotateCcw size={12} /> Reset
          </button>
          <button
            onClick={startPreview}
            disabled={playing || loading || scripts.length === 0}
            className="text-xs px-3 py-1 rounded-md bg-purple-600 hover:bg-purple-700 text-white font-semibold inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="maya-preview-play"
          >
            {playing ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            {playing ? 'Playing…' : (visibleMessages.length ? 'Replay' : `Play (${scripts.length})`)}
          </button>
        </div>

        {/* Chat preview */}
        <div className="flex-1 overflow-y-auto bg-gradient-to-b from-purple-50/30 to-white p-5 space-y-3" data-testid="maya-preview-chat">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : scripts.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-12" data-testid="maya-preview-empty">
              No active scripts for <code>{trigger}</code>. Author one in the Scripted Conversations tab to preview it here.
            </div>
          ) : visibleMessages.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-12">
              Press <strong>Play ({scripts.length})</strong> to start the preview.
            </div>
          ) : (
            <>
              {visibleMessages.map((m) => (
                <div key={m.id} className="flex items-start gap-2.5" data-testid={`maya-preview-bubble-${m.id}`}>
                  {identity?.portrait_url ? (
                    <img src={identity.portrait_url} alt="Maya" className="w-8 h-8 rounded-full object-cover bg-gray-100 flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-purple-200 flex-shrink-0" />
                  )}
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-md shadow-sm">
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{m.content}</p>
                  </div>
                </div>
              ))}
              {typing && (
                <div className="flex items-center gap-2.5" data-testid="maya-preview-typing">
                  {identity?.portrait_url && (
                    <img src={identity.portrait_url} alt="Maya" className="w-8 h-8 rounded-full object-cover bg-gray-100 flex-shrink-0" />
                  )}
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-2.5 shadow-sm">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-2.5 border-t border-gray-100 text-[11px] text-gray-400 bg-gray-50 rounded-b-2xl">
          Preview is read-only — no chat session is created and no delivery markers are written.
        </div>
      </div>
    </div>
  );
};

export default MayaPreviewModal;
